import { useEffect } from "react";

/**
 * Globally swaps every occurrence of the legacy Saudi Riyal glyph
 * (U+FDFC = ﷼) in rendered text for the new SAMA-issued symbol
 * (rendered as an inline-block masked PNG that inherits currentColor,
 * so it picks up the surrounding text color naturally).
 *
 * Why a runtime DOM swap instead of a font / direct edit:
 *   - The legacy ﷼ character is produced inside hundreds of formatted
 *     price strings (formatPrice, formatPriceRaw, third-party install-
 *     ment widget messages, blog content, admin labels, ...). Rewiring
 *     each call site would touch the entire codebase.
 *   - The new symbol is not yet in Unicode, so a pure CSS @font-face
 *     with `unicode-range: U+FDFC` would need a real font binary; we
 *     only have a PNG glyph. The mask-image trick reproduces the same
 *     result while letting the icon track text color and font size.
 *
 * Cost: an initial walk on mount + a MutationObserver on body for new
 * subtrees. We skip <input>, <textarea>, <code>, <pre>, <script>, etc.
 * and never re-enter our own injected spans, so churn stays bounded.
 */

const BASE_URL = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
const SAR_PNG_URL = `${BASE_URL}/saudi-riyal.png`;

const RIYAL_CHAR = "\uFDFC";

const SKIP_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "NOSCRIPT",
  "TEXTAREA",
  "INPUT",
  "CODE",
  "PRE",
  "TITLE",
  "SVG",
]);

const REPLACED_CLASS = "sar-symbol-inline";

function shouldSkip(el: Element | null): boolean {
  let cur: Element | null = el;
  while (cur) {
    if (SKIP_TAGS.has(cur.tagName)) return true;
    if ((cur as HTMLElement).isContentEditable) return true;
    if (cur.classList && cur.classList.contains(REPLACED_CLASS)) return true;
    cur = cur.parentElement;
  }
  return false;
}

function buildSpan(): HTMLSpanElement {
  const span = document.createElement("span");
  span.className = REPLACED_CLASS;
  span.setAttribute("aria-label", "ريال");
  span.setAttribute("role", "img");
  return span;
}

function processTextNode(node: Text): void {
  const text = node.nodeValue;
  if (!text || text.indexOf(RIYAL_CHAR) === -1) return;
  const parent = node.parentNode;
  if (!parent) return;

  // Goal: the riyal glyph must ALWAYS render on the VISUAL LEFT of its
  // adjacent number, regardless of the surrounding direction (RTL admin
  // dashboard cards, LTR English checkout summaries, mixed-direction
  // tooltips, etc).
  //
  // In an RTL parent the visual-left position is the DOM-END of the
  // run (text → span). In an LTR parent the visual-left position is
  // the DOM-START of the run (span → text). We detect the parent's
  // computed direction once and assemble the fragment accordingly.
  const parentEl = (node.parentElement || (parent as Element)) as Element | null;
  let dir = "ltr";
  try {
    if (parentEl && typeof window !== "undefined" && window.getComputedStyle) {
      dir = window.getComputedStyle(parentEl).direction || "ltr";
    }
  } catch {}
  const isRtl = dir === "rtl";

  const frag = document.createDocumentFragment();
  // Split the source text into runs of non-symbol text and count of
  // adjacent symbols. For each run we emit either [text, span] (RTL)
  // or [span, text] (LTR) so the icon is always visually to the left.
  let i = 0;
  while (i < text.length) {
    let txt = "";
    while (i < text.length && text[i] !== RIYAL_CHAR) {
      txt += text[i++];
    }
    let symCount = 0;
    while (i < text.length && text[i] === RIYAL_CHAR) {
      symCount++;
      i++;
    }
    // Trim a single trailing/leading space adjacent to the symbol so
    // we don't double-space around the icon's own CSS margin.
    if (isRtl) {
      const cleanTxt = symCount > 0 ? txt.replace(/\s+$/, "") : txt;
      if (cleanTxt) frag.appendChild(document.createTextNode(cleanTxt));
      for (let s = 0; s < symCount; s++) frag.appendChild(buildSpan());
    } else {
      const cleanTxt = symCount > 0 ? txt.replace(/^\s+/, "") : txt;
      for (let s = 0; s < symCount; s++) frag.appendChild(buildSpan());
      if (cleanTxt) frag.appendChild(document.createTextNode(cleanTxt));
    }
  }
  parent.replaceChild(frag, node);
}

function walk(root: Node): void {
  if (root.nodeType === Node.TEXT_NODE) {
    if (!shouldSkip((root as Text).parentElement)) processTextNode(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  if (shouldSkip(root as Element)) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const t = n.nodeValue;
      if (!t || t.indexOf(RIYAL_CHAR) === -1) return NodeFilter.FILTER_REJECT;
      if (shouldSkip((n as Text).parentElement)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const found: Text[] = [];
  let cur: Node | null;
  while ((cur = walker.nextNode())) found.push(cur as Text);
  found.forEach(processTextNode);
}

function injectStyle(): void {
  const id = "sar-symbol-style";
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = `.${REPLACED_CLASS}{
  display:inline-block;
  width:0.95em;
  height:0.95em;
  vertical-align:-0.16em;
  background-color:currentColor;
  -webkit-mask-image:url('${SAR_PNG_URL}');
  mask-image:url('${SAR_PNG_URL}');
  -webkit-mask-size:contain;
  mask-size:contain;
  -webkit-mask-repeat:no-repeat;
  mask-repeat:no-repeat;
  -webkit-mask-position:center;
  mask-position:center;
  margin-inline-start:0.05em;
  margin-inline-end:0.1em;
  flex-shrink:0;
}`;
  document.head.appendChild(style);
}

export function RiyalSymbolSwapper() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof MutationObserver === "undefined") return;

    injectStyle();

    // Initial pass over whatever has already been hydrated.
    if (document.body) walk(document.body);

    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData") {
          const t = m.target;
          if (t.nodeType === Node.TEXT_NODE && !shouldSkip((t as Text).parentElement)) {
            processTextNode(t as Text);
          }
          continue;
        }
        m.addedNodes.forEach((n) => {
          if (n.nodeType === Node.TEXT_NODE) {
            if (!shouldSkip((n as Text).parentElement)) processTextNode(n as Text);
          } else if (n.nodeType === Node.ELEMENT_NODE) {
            walk(n);
          }
        });
      }
    });
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => obs.disconnect();
  }, []);
  return null;
}
